import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
 * Cart count announcements are owned by `<header-actions>`.
 * This component handles the remaining cart-specific concerns: auto-open on add-to-cart,
 * sticky summary layout, and the installments CTA close-on-click.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    document.addEventListener('pointerdown', this.#handleOutsidePointerDown);
    document.addEventListener('wheel', this.#handleExternalScroll, { passive: true, capture: true });
    document.addEventListener('touchmove', this.#handleExternalScroll, { passive: true, capture: true });
    window.addEventListener?.('pagehide', this.#handleLifecycleDismiss);
    window.addEventListener?.('pageshow', this.#handleLifecycleDismiss);
    this.addEventListener('click', this.#handleCheckoutClick);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.#themeDrawer?.addEventListener('theme-drawer:close', this.#handleDrawerClose);

    // The restore path sets [open] before this module loads, so the
    // theme-drawer:open event will have already fired. Use the attribute
    // check so this works even before <theme-drawer> upgrades.
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    document.removeEventListener('pointerdown', this.#handleOutsidePointerDown);
    document.removeEventListener('wheel', this.#handleExternalScroll, true);
    document.removeEventListener('touchmove', this.#handleExternalScroll, true);
    window.removeEventListener?.('pagehide', this.#handleLifecycleDismiss);
    window.removeEventListener?.('pageshow', this.#handleLifecycleDismiss);
    this.removeEventListener('click', this.#handleCheckoutClick);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.#themeDrawer?.removeEventListener('theme-drawer:close', this.#handleDrawerClose);
    document.documentElement?.classList.remove('hc-cart-drawer-open');
  }

  /**
   * Handles the theme-drawer opening — updates sticky state and wires up the installments CTA.
   */
  #handleDrawerOpen = () => {
    document.documentElement?.classList.add('hc-cart-drawer-open');
    const checkoutButton = this.querySelector('button[name="checkout"]');
    this.#resetCheckoutButton(checkoutButton);
    this.#updateStickyState();

    // Close cart drawer when installments CTA is clicked to avoid overlapping dialogs.
    // Re-queried on every open so it survives cart content re-renders that
    // replace the shopify-payment-terms shadow root.
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.#themeDrawer?.close(), { once: true });
    });
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen = this.hasAttribute('auto-open') && event.action === 'add' && !this.#themeDrawer?.isOpen;

    // When the event originates inside an open MODAL <dialog> (e.g. quick-add),
    // defer the auto-open until that dialog's native `close` fires so its focus
    // restoration runs first — otherwise we'd capture the wrong
    // `#previouslyFocused`. Non-modal dialogs (e.g. the hotspot preview) don't
    // close on add and don't move focus, so `:modal` excludes them.
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );

    event.promise
      ?.then(async ({ cart, detail }) => {
        const settle = () => requestAnimationFrame(() => this.#updateStickyState());

        if (!shouldAutoOpen || detail?.didError) {
          settle();
          return;
        }

        const openAndSettle = async () => {
          if (!await this.#waitForCartReady(cart, detail)) {
            settle();
            return;
          }
          if (!this.#themeDrawer?.isOpen) this.#themeDrawer?.open();
          settle();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener('close', openAndSettle, { once: true });
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      });
  };

  #handleDrawerClose = () => {
    document.documentElement?.classList.remove('hc-cart-drawer-open');
  };

  #handleCheckoutClick = async (event) => {
    const button = event.target instanceof Element
      ? event.target.closest('button[name="checkout"][data-hc-checkout-required]')
      : null;
    if (!button || button.disabled) return;
    event.preventDefault();
    const label = button.querySelector('.button-text');
    button.dataset.checkoutDefaultText ||= label?.textContent?.trim() || 'Check out';
    button.classList.add('is-pending');
    button.setAttribute('aria-disabled', 'true');
    if (label) label.textContent = button.dataset.checkoutPendingText || 'Confirming delivery…';

    const editLink = this.querySelector('.cart-items__edit-configuration[href]');
    const cartEditUrl = editLink?.getAttribute('href') || button.dataset.hcConfiguratorUrl;
    await Promise.race([
      customElements.whenDefined('honeycomb-configurator'),
      new Promise((resolve) => setTimeout(resolve, 1500)),
    ]);
    const configurator = document.querySelector('honeycomb-configurator[data-hc-checkout-endpoint]');

    if (configurator && typeof configurator.openServerPricedCheckout === 'function') {
      try {
        const root = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${root}cart.js`, { headers: { accept: 'application/json' } });
        if (!response.ok) throw new Error('Cart could not be loaded.');
        const cartPayload = await response.json();
        this.#themeDrawer?.close();
        requestAnimationFrame(() => {
          configurator.openServerPricedCheckout({ cartPayload });
          this.#resetCheckoutButton(button, label);
        });
        return;
      } catch (error) {
        console.warn('[cart-drawer] Delivery checkout could not read the cart.', error);
      }
    }

    if (cartEditUrl) {
      const checkoutUrl = new URL(cartEditUrl, window.location.origin);
      checkoutUrl.searchParams.set('hc_checkout', '1');
      window.location.assign(checkoutUrl.href);
      return;
    }

    this.#resetCheckoutButton(button, label);
  };

  #resetCheckoutButton(button, label = null) {
    if (!(button instanceof Element)) return;
    button.classList.remove('is-pending');
    button.removeAttribute('aria-disabled');
    const buttonLabel = label || button.querySelector('.button-text');
    if (buttonLabel) buttonLabel.textContent = button.dataset.checkoutDefaultText || 'Check out';
  }

  #handleOutsidePointerDown = (event) => {
    const drawer = this.#themeDrawer;
    const panel = this.#dialog;
    if (!drawer?.isOpen || !panel) return;

    const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
    if (path.includes(panel) || panel.querySelector('dialog[open]')) return;

    drawer.close();
  };

  #handleExternalScroll = (event) => {
    const drawer = this.#themeDrawer;
    const panel = this.#dialog;
    if (!drawer?.isOpen || !panel) return;
    const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
    if (path.includes(panel)) return;
    drawer.close();
  };

  #handleLifecycleDismiss = () => {
    if (this.#themeDrawer?.isOpen) this.#themeDrawer.close();
  };

  async #waitForCartReady(cart, detail) {
    const expectedQuantity = Number(cart?.totalQuantity);
    const expectedKeys = Array.isArray(detail?.items)
      ? detail.items.map((item) => item?.key).filter(Boolean)
      : [];
    if (!Number.isFinite(expectedQuantity) && expectedKeys.length === 0) {
      await new Promise((resolve) => requestAnimationFrame(resolve));
      return true;
    }

    const isReady = () => {
      const renderedKeys = new Set(
        Array.from(this.querySelectorAll('[data-key]'))
          .map((row) => row.dataset?.key)
          .filter(Boolean),
      );
      const renderedQuantity = Number(this.querySelector('.theme-drawer__badge')?.textContent?.trim());
      const hasExpectedLines = expectedKeys.length === 0
        ? !this.#isCartEmpty()
        : expectedKeys.every((key) => renderedKeys.has(key));
      const hasExpectedQuantity = !Number.isFinite(expectedQuantity)
        || renderedQuantity === expectedQuantity;

      return hasExpectedLines && hasExpectedQuantity;
    };

    if (isReady()) return true;

    return new Promise((resolve) => {
      let settled = false;
      const finish = (ready) => {
        if (settled) return;
        settled = true;
        observer.disconnect();
        clearTimeout(timeout);
        resolve(ready);
      };
      const observer = new MutationObserver(() => {
        if (isReady()) finish(true);
      });
      const timeout = setTimeout(() => finish(isReady()), 900);
      observer.observe(this, { childList: true, subtree: true, characterData: true });
    });

  }

  #isCartEmpty() {
    return Boolean(this.querySelector('.cart-drawer--empty'));
  }

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
